import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { ReactComponent as Logo } from "../../assets/logo/zecado.svg";
import "./navbar.css";

const NAV_LINKS = [
  { name: "Shop", path: "/shop" },
  { name: "About", path: "/about" },
  { name: "Contact", path: "/contact" },
];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuSearchTerm, setMenuSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  const cart = useSelector((state) => state.cart.cart);
  const navigate = useNavigate();
  const location = useLocation();
  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchTerm("");
  };

  // autoFocus is unreliable on an element that mounts inside an animating
  // container, so the focus is driven explicitly once the panel is open.
  useEffect(() => {
    if (isSearchOpen) searchInputRef.current?.focus();
  }, [isSearchOpen]);

  // The only search route is /search/:query. The header icon used to navigate to
  // a bare "/search", which has no route and landed on the 404 page. Queries are
  // encoded because the term sits in a path segment, so a space or a slash would
  // otherwise produce a broken or wrong URL.
  const goToSearch = (raw) => {
    const query = (raw || "").trim();
    if (!query) return;
    navigate(`/search/${encodeURIComponent(query)}`);
    setIsMenuOpen(false);
    closeSearch();
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ease-in-out ${
          isScrolled ? "h-16 border-b border-gray-100" : "h-24"
        }`}
      >
        {/* ✅ FIXED: Updated padding to match Hero (px-4 md:px-8 lg:px-12) */}
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-12 h-full flex items-center justify-between relative">
          
          {/* MOBILE: Hamburger */}
          <button 
            className="lg:hidden p-2 -ml-2 text-black"
            onClick={() => setIsMenuOpen(true)}
            aria-label="Open Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth="1.2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* DESKTOP: Left Links */}
          <nav className="hidden lg:flex items-center gap-12 w-1/3">
            {NAV_LINKS.slice(0, 2).map((link) => (
              <NavLink 
                key={link.name} 
                to={link.path}
                className={({ isActive }) => `
                  text-[11px] uppercase tracking-[0.2em] font-medium transition-colors duration-300
                  ${isActive ? "text-black" : "text-gray-600 hover:text-black"} 
                `}
              >
                {link.name}
              </NavLink>
            ))}
          </nav>

          {/* CENTER: Logo */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <NavLink to="/">
              <Logo 
                className={`transition-all duration-300 text-black fill-black ${
                  isScrolled ? "h-5" : "h-7"
                }`} 
              />
            </NavLink>
          </div>

          {/* RIGHT SIDE: Links + Icons */}
          <div className="flex items-center justify-end gap-8 w-1/3">
            <nav className="hidden lg:flex items-center gap-12">
              {NAV_LINKS.slice(2).map((link) => (
                <NavLink 
                  key={link.name} 
                  to={link.path}
                  className={({ isActive }) => `
                    text-[11px] uppercase tracking-[0.2em] font-medium transition-colors duration-300
                    ${isActive ? "text-black" : "text-gray-600 hover:text-black"}
                  `}
                >
                  {link.name}
                </NavLink>
              ))}
            </nav>

            {/* Icons */}
            <div className="flex items-center gap-6">
              <button
                onClick={() => setIsSearchOpen(true)}
                aria-label="Search"
                aria-expanded={isSearchOpen}
                className="text-black hover:opacity-60 transition-opacity"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              <button onClick={() => navigate("/cart")} className="text-black hover:opacity-60 transition-opacity flex items-center gap-2">
                <div className="relative">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-black text-white text-[9px] flex items-center justify-center rounded-full">
                      {cartCount}
                    </span>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* SEARCH PANEL
              Spans the whole header rather than sitting inline with the icons.
              Inline was unusable on a phone: a 160px input inside a w-1/3 flex
              column has about 125px to work with at 375px wide, so it overflowed
              the row and ran under the absolutely-centred logo. Covering the bar
              is also the pattern shoppers already know from other storefronts.

              inset-0 resolves against the padding box of the relatively
              positioned container, so the padding classes are repeated here to
              line the field up with the rest of the page content. */}
          <AnimatePresence>
            {isSearchOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="absolute inset-0 z-10 bg-white flex items-center px-4 md:px-8 lg:px-12"
              >
                {/* A real form, so the virtual keyboard shows a Go/Search key and
                    submitting with it actually works. Listening for Enter on
                    keydown alone is unreliable on mobile browsers. */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    goToSearch(searchTerm);
                  }}
                  className="flex items-center gap-4 w-full"
                  role="search"
                >
                  <svg className="w-5 h-5 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>

                  <input
                    ref={searchInputRef}
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search fragrances"
                    placeholder="Search fragrances"
                    enterKeyHint="search"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck="false"
                    onKeyDown={(e) => {
                      if (e.key === "Escape") closeSearch();
                    }}
                    /* 16px is not a style choice: iOS Safari zooms the whole
                       page when a focused input is smaller than that, and the
                       header's 11px type triggered it on every tap. */
                    className="flex-1 min-w-0 bg-transparent text-[16px] tracking-[0.05em] text-black outline-none placeholder-gray-400"
                  />

                  {/* Closing on blur lost whatever had been typed the moment the
                      field lost focus, which on a phone happens constantly. It
                      closes on an explicit tap, on Escape, or on navigation. */}
                  <button
                    type="button"
                    onClick={closeSearch}
                    aria-label="Close search"
                    className="p-2 -mr-2 shrink-0 text-black hover:opacity-60 transition-opacity"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth="1.2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* --- MOBILE MENU --- */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed inset-0 z-50 bg-white flex flex-col lg:hidden"
          >
            <div className="h-24 flex items-center justify-between px-6">
               <span className="text-[10px] uppercase tracking-widest text-gray-500">Menu</span>
               <button 
                 onClick={() => setIsMenuOpen(false)}
                 className="p-2 -mr-2 text-black hover:opacity-60 transition-opacity"
               >
                 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth="1.2" d="M6 18L18 6M6 6l12 12" />
                 </svg>
               </button>
            </div>

            <div className="flex-1 flex flex-col justify-center items-center space-y-8 pb-20">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.name}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={({ isActive }) => `
                    text-3xl font-light tracking-wide transition-all duration-300
                    ${isActive ? "text-black scale-105" : "text-gray-500 hover:text-black"}
                  `}
                >
                  {link.name}
                </NavLink>
              ))}
              
              {/* Same treatment as the header panel: a real form so the phone
                  keyboard's Go key submits, and 16px type so focusing it does
                  not make iOS Safari zoom the menu. max-w keeps it inside the
                  viewport on a small phone, where a fixed w-64 plus the menu's
                  padding was already tight. */}
              <form
                role="search"
                onSubmit={(e) => {
                  e.preventDefault();
                  goToSearch(menuSearchTerm);
                }}
                className="mt-8 w-full max-w-xs px-8"
              >
                <div className="flex items-center gap-3 border-b border-gray-200 py-2">
                  <input
                    type="search"
                    value={menuSearchTerm}
                    onChange={(e) => setMenuSearchTerm(e.target.value)}
                    aria-label="Search fragrances"
                    placeholder="Search fragrances"
                    enterKeyHint="search"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck="false"
                    className="w-full min-w-0 bg-transparent outline-none text-[16px] tracking-[0.05em] placeholder-gray-400 text-black"
                  />
                  <button
                    type="submit"
                    aria-label="Search"
                    className="shrink-0 text-black hover:opacity-60 transition-opacity"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>
              </form>
            </div>

            <div className="p-8 text-center">
              <div className="flex justify-center gap-6 text-[10px] uppercase tracking-widest text-gray-400">
                <a href="#">Account</a>
                <a href="#">Instagram</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;